import React, { useState, useEffect } from 'react';
import api from '../api';
import { UploadSimple, File, Trash, CheckCircle, X } from '@phosphor-icons/react';

export default function EvidencePanel({ linkedRecord, linkedType, onUpload }) {
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [description, setDescription] = useState('');
  const [evidenceType, setEvidenceType] = useState('');
  const [success, setSuccess] = useState('');

  const fetchDocs = async () => {
    try {
      const params = new URLSearchParams();
      if (linkedRecord) params.append('linked_record', linkedRecord);
      if (linkedType) params.append('linked_type', linkedType);
      const { data } = await api.get(`/api/evidence?${params}`);
      setDocuments(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchDocs(); }, [linkedRecord, linkedType]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('linked_record', linkedRecord || '');
      formData.append('linked_type', linkedType || '');
      formData.append('evidence_type', evidenceType);
      formData.append('description', description);
      await api.post('/api/evidence/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess('File uploaded successfully');
      setDescription('');
      setEvidenceType('');
      setShowUpload(false);
      fetchDocs();
      if (onUpload) onUpload();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this evidence document?')) return;
    try {
      await api.delete(`/api/evidence/${id}`);
      fetchDocs();
    } catch { /* ignore */ }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div data-testid="evidence-panel" className="bg-white border border-border-default rounded-lg">
      <div className="flex items-center justify-between px-5 py-3 border-b border-border-default">
        <h4 className="text-sm font-semibold text-content-primary font-heading">Evidence Documents</h4>
        <button
          data-testid="evidence-upload-btn"
          onClick={() => setShowUpload(!showUpload)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-primary text-white rounded-lg text-xs font-semibold hover:bg-brand-primary-hover transition-all"
        >
          <UploadSimple size={14} />
          Upload
        </button>
      </div>

      {success && (
        <div className="mx-5 mt-3 flex items-center gap-2 p-2 bg-status-on-track-bg text-status-on-track text-xs rounded-lg">
          <CheckCircle size={14} weight="fill" /> {success}
        </div>
      )}

      {showUpload && (
        <div className="px-5 py-3 border-b border-border-subtle bg-surface-default">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-[10px] font-semibold text-content-secondary uppercase tracking-wider mb-1">Evidence Type</label>
              <select
                data-testid="evidence-type-select"
                value={evidenceType}
                onChange={e => setEvidenceType(e.target.value)}
                className="w-full px-2.5 py-2 border border-border-default rounded-md text-xs bg-white focus:outline-none focus:ring-2 focus:ring-brand-secondary/50"
              >
                <option value="">Select type</option>
                <option value="Utility Bill">Utility Bill</option>
                <option value="Meter Export">Meter Export</option>
                <option value="Hauler Certificate">Hauler Certificate</option>
                <option value="BMS Export">BMS Export</option>
                <option value="Work Order">Work Order</option>
                <option value="TOR/Specification">TOR/Specification</option>
                <option value="Supplier Certificate">Supplier Certificate</option>
                <option value="Assessment Report">Assessment Report</option>
                <option value="Approval Log">Approval Log</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-content-secondary uppercase tracking-wider mb-1">Description</label>
              <input
                data-testid="evidence-description-input"
                type="text"
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="w-full px-2.5 py-2 border border-border-default rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-brand-secondary/50"
                placeholder="Brief description..."
              />
            </div>
          </div>
          <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-brand-secondary/40 rounded-lg cursor-pointer hover:bg-brand-primary/5 transition-all">
            <UploadSimple size={18} className="text-brand-primary" />
            <span className="text-xs font-medium text-content-secondary">
              {uploading ? 'Uploading...' : 'Click to select file'}
            </span>
            <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="evidence-file-input" />
          </label>
        </div>
      )}

      <div className="max-h-[200px] overflow-y-auto">
        {documents.length === 0 ? (
          <p className="px-5 py-4 text-xs text-content-disabled text-center">No evidence documents attached</p>
        ) : (
          <div className="divide-y divide-border-subtle">
            {documents.map(doc => (
              <div key={doc.id} className="flex items-center gap-3 px-5 py-2.5 hover:bg-surface-muted/50 transition-colors">
                <File size={16} className="text-brand-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-content-primary truncate">{doc.file_name}</p>
                  <p className="text-[10px] text-content-disabled">
                    {doc.evidence_type && <span className="text-brand-primary font-medium">{doc.evidence_type}</span>}
                    {doc.evidence_type && ' · '}{formatSize(doc.file_size)} · {doc.uploaded_by_name}
                  </p>
                </div>
                <button
                  onClick={() => handleDelete(doc.id)}
                  className="p-1 rounded hover:bg-status-alert-bg transition-colors text-content-disabled hover:text-status-alert"
                >
                  <Trash size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
